import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { PageShell } from "../components/layout/PageShell";
import { CtaBand } from "../components/layout/CtaBand";

const FAQS = [
  {
    q: "How do I book?",
    a: "Submit an enquiry with details about your pet, the service you need and your preferred dates or regular days. Carly will discuss availability and suitability before confirming the next steps. Nothing is booked automatically.",
  },
  {
    q: "Do you offer a meet and greet?",
    a: "Yes. Mary Pawpins offers a free meet and greet so Carly can get to know your pet and discuss their routine, temperament and care requirements before a booking is agreed.",
  },
  {
    q: "What days do you offer dog walking?",
    a: "Regular dog walking is currently focused on Mondays, Tuesdays and Fridays. Availability for home visits and pet sitting is discussed individually.",
  },
  {
    q: "Where do you cover?",
    a: "Dog walking is offered across Inverness, subject to route and availability. Pet-sitting locations are considered individually, with travel requirements taken into account.",
  },
  {
    q: "Is Mary's Meadow fully secure?",
    a: "Mary's Meadow is fully fenced, although the fencing is not six feet high. Dogs with a history of jumping, climbing or escaping are assessed individually to make sure the field is suitable for them. Not every dog will be suitable for off-lead exercise.",
  },
  {
    q: "Do all walks take place at Mary's Meadow?",
    a: "No. Mary's Meadow is the main walking location, but other fully fenced fields are sometimes used for variety, and selected smaller groups may enjoy forest trails where appropriate.",
  },
  {
    q: "How are dogs matched into groups?",
    a: "Carly considers temperament, energy, play style, previous experience and individual needs. A meet and greet and a suitable introduction help determine whether a group is the right fit.",
  },
  {
    q: "Does my dog need good recall?",
    a: "Not for Mary's Meadow! Our fully fenced field gives dogs plenty of freedom to explore without needing perfect recall. For dogs who may try to jump fences, a long line can provide extra security while still allowing them room to enjoy themselves.\n\nDogs with reliable recall can also enjoy a wider variety of walks, including forest trails and other countryside routes. Every walk is planned around your dog's individual needs and safety.",
  },
  {
    q: "How many dogs are in a group?",
    a: "Group sizes vary according to the dogs and the environment. Mary Pawpins operates within its insurance conditions. The maximums in the policy are upper limits, not the number included in every group.",
  },
  {
    q: "How long will my dog be away from home?",
    a: "It depends on the collection route, your location and the agreed service. Carly will give you a realistic window when your regular place is arranged.",
  },
  {
    q: "Will I receive updates?",
    a: "Yes. Photographs and updates are part of Carly's usual communication with clients. The exact frequency varies with the service and booking arrangements.",
  },
  {
    q: "What happens in bad weather?",
    a: "Walks and activities are assessed according to the conditions and the needs of the dogs involved. Safety takes priority, and Carly will communicate any necessary changes.",
  },
  {
    q: "Do you offer boarding in your own home?",
    a: "No. Mary Pawpins offers in-home pet sitting in your property, so your pets remain in their familiar surroundings.",
  },
  {
    q: "Will you be with my dog for the full 24 hours during Gold sitting?",
    a: "Gold is a 24-hour care booking, but it doesn't mean uninterrupted supervision. The package includes a minimum of three walks a day, and Carly may be away for up to two hours. The routine and absence arrangements are agreed before booking.",
  },
  {
    q: "Can my dog join your regular walks while you are sitting?",
    a: "Where suitable, yes, with your agreement. Dogs who don't join must be comfortable remaining at home during agreed absences. Suitability is always discussed before a booking is accepted.",
  },
  {
    q: "Can you look after cats and other animals?",
    a: "Yes. Mary Pawpins offers home visits and care for cats and other small domestic animals, subject to the species, care requirements and Carly's experience and availability.",
  },
  {
    q: "Can you administer medication?",
    a: "Medication may be considered where it is agreed in advance, appropriate to Carly's competence and supported by clear instructions. Any specialist requirements should be discussed before booking.",
  },
  {
    q: "Are you insured?",
    a: "Yes. Mary Pawpins holds commercial pet-business insurance, including public liability and animal injury cover, subject to the policy terms and limits. Details can be provided on request.",
  },
  {
    q: "What are your cancellation and payment policies?",
    a: "Current payment terms, notice periods and cancellation arrangements are shared and agreed when your booking is confirmed, so you always receive the up-to-date terms.",
  },
];

const FaqItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#DDD7CB]" data-testid={`faq-item-${index}`}>
      <button
        className="w-full flex items-center justify-between gap-6 py-5 text-left group"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid={`faq-question-${index}`}
      >
        <span className="font-medium text-[#292A26] group-hover:text-[#3F4A38] transition-colors">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#3F4A38] flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-6 pr-10 space-y-3" data-testid={`faq-answer-${index}`}>
          {a.split("\n\n").map((para, i) => (
            <p key={i} className="text-sm leading-relaxed text-[#575953]">{para}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default function FaqsPage() {
  return (
    <PageShell title="FAQs">
      <section className="pt-20 pb-8 sm:pt-28 sm:pb-10" data-testid="faqs-hero">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <p className="eyebrow">FAQs</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium text-[#292A26] tracking-tight mt-4" data-testid="faqs-heading">
            Good questions.
          </h1>
          <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#575953] max-w-2xl">
            The practical details, answered plainly. Anything else, just ask. Carly
            replies to every enquiry personally.
          </p>
        </div>
      </section>

      <section className="pb-8" data-testid="faqs-list">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <div className="bg-white rounded-3xl border border-[#DDD7CB] px-7 sm:px-10 py-4">
            {FAQS.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} index={i} />
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        testId="faqs-cta"
        heading="Still wondering about something?"
        copy="Send an enquiry with your question and Carly will come back to you personally."
      />
    </PageShell>
  );
}
